// D3D-774: 提案経路一覧パネルと PoC ダイアログで共有する、目的ラベル・指標整形・診断日本語化のユーティリティ。

import {
	ROAD_AI_METRIC_NOT_APPLICABLE_LABEL,
	isDesignDependentMetric,
} from "@/components/Project/road-ai/roadAi.types";

// 目的（objectives）の英語キー → ユーザー向け日本語ラベル
export const OBJECTIVE_LABELS = {
	cut: "切土最小",
	fill: "盛土最小",
	over_cut: "掘りすぎ回避",
	over_fill: "盛りすぎ回避",
	cf_diff: "切盛バランス",
};

/**
 * 目的キーを日本語ラベルへ変換する。未知キーはそのまま返す。
 * @param {string} key 目的キー
 * @returns {string} 日本語ラベル
 */
export const objectiveLabel = (key) => OBJECTIVE_LABELS[key] || key;

/**
 * 候補の確定土量（metrics_final）の指定指標を小数第1位で整形する。
 * @param {Object} candidate 候補
 * @param {string} key 指標キー
 * @returns {string} 整形済み文字列（数値でなければ "-"）
 */
export const formatMetric = (candidate, key) => {
	const v = candidate?.metrics_final?.[key];
	return typeof v === "number" ? v.toFixed(1) : "-";
};

/**
 * モードを考慮して指標セルの表示を決める。
 * 点群のみモードの設計依存指標は「対象外」（値 0 と区別する。AC-7）、
 * 値が取得できない場合は「-」（不明）とする。
 *
 * @param {Object} candidate 候補
 * @param {string} key 指標キー
 * @param {string | null} mode "standard" | "point_cloud_only" | null
 * @returns {import("@/components/Project/road-ai/roadAi.types").RoadAiMetricDisplay} 表示内容
 */
export const formatMetricForMode = (candidate, key, mode) => {
	const value = candidate?.metrics_final?.[key];
	if (typeof value === "number") {
		return { kind: "value", text: value.toFixed(1) };
	}
	if (mode === "point_cloud_only" && isDesignDependentMetric(key)) {
		return {
			kind: "not_applicable",
			text: ROAD_AI_METRIC_NOT_APPLICABLE_LABEL,
		};
	}
	// 設計依存指標が null で返るのは点群のみモードのため、標準モードの null は不明扱い。
	if (value === null && isDesignDependentMetric(key)) {
		return {
			kind: "not_applicable",
			text: ROAD_AI_METRIC_NOT_APPLICABLE_LABEL,
		};
	}
	return { kind: "unknown", text: "-" };
};

/**
 * 裁量（フォールバック）候補の違反内容を要約する。
 * @param {Object} candidate 候補
 * @returns {string} 違反要約
 */
export const violationSummary = (candidate) => {
	const names = { road_width_m: "道路幅員", slope_ratio: "横断法面" };
	const vs = Array.isArray(candidate?.violations) ? candidate.violations : [];
	if (!vs.length) return "制約違反あり";
	return `違反: ${vs.map((v) => names[v.type] || v.type).join(" / ")}`;
};

/**
 * 診断コードを日本語の理由・詳細・対処に変換する。
 * @param {Object} d 診断（{ code, message, detail }）
 * @returns {{ code: string, level: string, title: string, detail: string, hint: string }}
 */
export const describeDiagnostic = (d) => {
	const code = d?.code || "";
	const det = d?.detail || {};
	const pct = (v) => (typeof v === "number" ? `${(v * 100).toFixed(1)}%` : "-");
	const map = {
		OBJECTIVE_ROUTE_NOT_FOUND: {
			level: "error",
			title: "経路が見つかりませんでした",
			detail: `目的「${det.objective ?? "-"}」／理由: ${det.reason ?? "-"}`,
			hint: "点群データの濃い範囲で、なるべく近い2点を選び直してください。",
		},
		NO_GROUND_CELL: {
			level: "warning",
			title: "探索範囲に地盤データ(点群)の無いセルがあります",
			detail: `無地盤ノード率 ${pct(det.no_ground_xy_ratio)}（${det.no_ground_xy_node_count ?? "-"}ノード）`,
			hint: "点群が疎な領域です。データの濃い範囲で選び直してください。",
		},
		TARGET_PARTIAL_COVERAGE: {
			level: "warning",
			title: "設計データのカバー範囲が部分的です",
			detail: `設計なしノード率 ${pct(det.no_design_ratio)}`,
			hint: "設計データの範囲内で始点終点を選ぶと安定します。",
		},
		START_NO_GROUND_CELL: {
			level: "error",
			title: "始点が地盤データの無い位置です",
			detail: "",
			hint: "始点を点群データのある範囲で選び直してください。",
		},
		END_NO_GROUND_CELL: {
			level: "error",
			title: "終点が地盤データの無い位置です",
			detail: "",
			hint: "終点を点群データのある範囲で選び直してください。",
		},
		START_OUTSIDE_DESIGN_COVERAGE: {
			level: "error",
			title: "始点が設計データの範囲外です",
			detail: "",
			hint: "始点を設計データの範囲内で選び直してください。",
		},
		END_OUTSIDE_DESIGN_COVERAGE: {
			level: "error",
			title: "終点が設計データの範囲外です",
			detail: "",
			hint: "終点を設計データの範囲内で選び直してください。",
		},
		TOP_K_CANDIDATE_SHORTAGE: {
			level: "warning",
			title: "候補数が不足しています",
			detail: `目的「${det.objective ?? "-"}」要求${det.requested_k ?? "-"}件／取得${det.returned_k ?? "-"}件`,
			hint: "",
		},
		SLOPE_CONSTRAINT_VIOLATION: {
			level: "warning",
			title: "縦断勾配の制約を超過しました",
			detail: "",
			hint: "最大縦断勾配を緩めるか、標高差の小さい2点にしてください。",
		},
		LONGITUDINAL_BAND_RELAXED: {
			level: "info",
			title: "縦断バンドを緩和して解を得ました（情報）",
			detail: det.objective ? `目的「${det.objective}」` : "",
			hint: "",
		},
		FALLBACK_CANDIDATE_RETURNED: {
			level: "warning",
			title: "適合経路が無いため、違反ありの裁量経路を返しました",
			detail: `目的「${det.objective ?? "-"}」／違反: ${(det.violations || []).join(" / ") || "-"}`,
			hint: "描画は可能ですが制約違反を含みます。パラメータや2点を調整すると適合経路が出る場合があります。",
		},
		WAYPOINT_OUT_OF_RANGE: {
			level: "error",
			title: `中継点${det.waypoint_number ?? "-"} が探索範囲外です`,
			detail: "",
			hint: "中継点をデータ範囲内に置き直してください。",
		},
		WAYPOINT_NO_GROUND_CELL: {
			level: "error",
			title: `中継点${det.waypoint_number ?? "-"} が点群の無い位置です`,
			detail: "",
			hint: "点群のある範囲に中継点を置き直してください。",
		},
		WAYPOINT_OUTSIDE_DESIGN_COVERAGE: {
			level: "warning",
			title: `中継点${det.waypoint_number ?? "-"} が設計データの範囲外です`,
			detail: "",
			hint: "設計データ範囲内に中継点を置くと安定します。",
		},
		WAYPOINT_UNREACHABLE: {
			level: "error",
			title: `中継点${det.waypoint_number ?? "-"} で経路が見つかりませんでした`,
			detail: det.objective ? `目的「${det.objective}」` : "",
			hint: "中継点の順序・位置を調整するか、通過半径を広げてください。",
		},
	};
	const base = map[code];
	if (base) return { code, ...base };
	if (code.startsWith("WIDTH")) {
		return {
			code,
			level: "warning",
			title: "幅員の制約を満たせません",
			detail: d?.message || "",
			hint: "道路幅員を小さくするか、余裕のある2点にしてください。",
		};
	}
	return {
		code,
		level: "warning",
		title: d?.message || code || "診断",
		detail: "",
		hint: "",
	};
};
